import React, { useEffect, useState } from 'react';
import { DownloadCloud, FileUp, RefreshCw, Rss, UploadCloud } from 'lucide-react';
import { api, ApiError } from '../api';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';

interface Subscription {
  id: string;
  url: string;
  name: string;
  color: string;
  status: 'ACTIVE' | 'PAUSED' | 'ERROR';
  lastSyncedAt?: string | null;
  lastError?: string | null;
  calendar: { id: string; name: string; color: string; enabled: boolean; _count?: { events: number } };
}

interface Calendar {
  id: string;
  name: string;
  color: string;
  accessRole?: 'OWNER' | 'WRITE' | 'READ';
}

interface ImportResult {
  ok: boolean;
  imported: number;
  updated: number;
  skipped: number;
  error?: string;
  calendar?: { id: string; name: string };
}

export function SubscriptionsPage() {
  const [items, setItems] = useState<Subscription[]>([]);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [form, setForm] = useState({ name: '', url: '', color: '#14B8A6' });
  const [importForm, setImportForm] = useState({
    sourceMode: 'FILE' as 'FILE' | 'URL' | 'TEXT',
    url: '',
    icsText: '',
    targetMode: 'NEW' as 'NEW' | 'EXISTING',
    calendarId: '',
    calendarName: 'Google Import',
    color: '#3B82F6',
    timezone: 'Europe/Berlin',
  });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const toast = useToast();

  const load = async () => {
    const [subs, cals] = await Promise.all([
      api.get<Subscription[]>('/api/subscriptions'),
      api.get<Calendar[]>('/api/calendars'),
    ]);
    setItems(subs);
    setCalendars(cals.filter((cal) => cal.accessRole !== 'READ'));
    setImportForm((current) => ({
      ...current,
      calendarId: current.calendarId || cals.find((cal) => cal.accessRole !== 'READ')?.id || '',
    }));
  };
  useEffect(() => {
    load();
  }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post<{ sync: { ok: boolean; imported: number; error?: string } }>('/api/subscriptions', form);
      toast.success(res.sync.ok ? `${res.sync.imported} Termine importiert` : `Abo angelegt, Sync-Fehler: ${res.sync.error}`);
      setForm({ name: '', url: '', color: '#14B8A6' });
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Abo konnte nicht angelegt werden');
    }
  };

  const sync = async (id: string) => {
    const res = await api.post<{ ok: boolean; imported: number; error?: string }>(`/api/subscriptions/${id}/sync`);
    res.ok ? toast.success(`${res.imported} Termine synchronisiert`) : toast.error(res.error ?? 'Sync fehlgeschlagen');
    load();
  };

  const remove = async (id: string) => {
    await api.delete(`/api/subscriptions/${id}`);
    toast.success('Abo geloescht');
    load();
  };

  const importIcs = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setImporting(true);
      setImportResult(null);
      const sourceType = importForm.sourceMode === 'URL' ? 'URL' : 'TEXT';
      const result = await api.post<ImportResult>('/api/imports/ics', {
        sourceType,
        url: sourceType === 'URL' ? importForm.url : undefined,
        icsText: sourceType === 'TEXT' ? importForm.icsText : undefined,
        calendarId: importForm.targetMode === 'EXISTING' ? importForm.calendarId : undefined,
        calendarName: importForm.targetMode === 'NEW' ? importForm.calendarName : undefined,
        color: importForm.color,
        timezone: importForm.timezone,
      });
      setImportResult(result);
      if (result.ok) {
        toast.success(`${result.imported} importiert, ${result.updated} aktualisiert`);
        await load();
      } else {
        toast.error(result.error ?? 'Import fehlgeschlagen');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  const loadFile = async (file?: File) => {
    if (!file) return;
    const text = await file.text();
    setImportForm((current) => ({
      ...current,
      sourceMode: 'FILE',
      icsText: text,
      calendarName: current.calendarName || file.name.replace(/\.ics$/i, ''),
    }));
    toast.success(`${file.name} geladen`);
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="overflow-hidden rounded-xl border border-border bg-[linear-gradient(180deg,rgba(255,255,255,0.075),rgba(255,255,255,0.025))] shadow-card">
        <div className="p-5">
          <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-border bg-bg/45 px-3 py-1.5 text-small text-ink-secondary">
            <DownloadCloud size={15} />Import & Abos
          </div>
          <h1 className="text-h2">Kalender importieren oder abonnieren</h1>
          <div className="mt-2 text-body text-ink-secondary">Google, Apple, Outlook und andere ICS-Kalender koennen als echte Kalender migriert oder als Feed synchronisiert werden.</div>
        </div>
      </section>

      <form onSubmit={importIcs} className="rounded-xl border border-border bg-surface-card p-5 shadow-card">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="inline-flex items-center gap-2 text-h4"><UploadCloud size={18} />Bestehenden Kalender importieren</h2>
            <div className="mt-1 text-small text-ink-muted">Einmalige Migration inklusive alter Termine. Re-Import aktualisiert bekannte UIDs im Zielkalender.</div>
          </div>
          <Badge tone="info">Migration</Badge>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr),minmax(320px,0.9fr)]">
          <section className="grid gap-3 rounded-lg border border-border bg-surface-elevated/60 p-4">
            <div className="flex flex-wrap gap-2">
              {(['FILE', 'URL', 'TEXT'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`rounded-md border px-3 py-2 text-small ${importForm.sourceMode === mode ? 'border-brand-primary/50 bg-brand-primary/15 text-ink-primary' : 'border-border bg-bg/30 text-ink-muted'}`}
                  onClick={() => setImportForm({ ...importForm, sourceMode: mode })}
                >
                  {mode === 'FILE' ? 'ICS-Datei' : mode === 'URL' ? 'ICS-URL' : 'ICS-Text'}
                </button>
              ))}
            </div>

            {importForm.sourceMode === 'FILE' && (
              <label className="grid cursor-pointer gap-2 rounded-lg border border-dashed border-border bg-bg/30 p-5 text-center hover:bg-white/[0.04]">
                <FileUp size={24} className="mx-auto text-ink-secondary" />
                <span className="font-medium">Google-/ICS-Datei auswaehlen</span>
                <span className="text-small text-ink-muted">{importForm.icsText ? `${Math.round(importForm.icsText.length / 1024)} KB bereit` : 'Google Takeout oder Exportdatei mit .ics'}</span>
                <input type="file" accept=".ics,text/calendar" className="sr-only" onChange={(e) => loadFile(e.target.files?.[0])} />
              </label>
            )}

            {importForm.sourceMode === 'URL' && (
              <div>
                <label className="label">Private ICS-URL</label>
                <input className="input font-mono" value={importForm.url} onChange={(e) => setImportForm({ ...importForm, url: e.target.value })} placeholder="https://calendar.google.com/calendar/ical/..." />
              </div>
            )}

            {importForm.sourceMode === 'TEXT' && (
              <div>
                <label className="label">ICS-Inhalt</label>
                <textarea className="textarea font-mono" rows={8} value={importForm.icsText} onChange={(e) => setImportForm({ ...importForm, icsText: e.target.value })} placeholder="BEGIN:VCALENDAR..." />
              </div>
            )}
          </section>

          <section className="grid gap-3 rounded-lg border border-border bg-surface-elevated/60 p-4">
            <div>
              <label className="label">Ziel</label>
              <select className="select" value={importForm.targetMode} onChange={(e) => setImportForm({ ...importForm, targetMode: e.target.value as 'NEW' | 'EXISTING' })}>
                <option value="NEW">Neuen Kalender anlegen</option>
                <option value="EXISTING">In bestehenden Kalender importieren</option>
              </select>
            </div>
            {importForm.targetMode === 'NEW' ? (
              <>
                <div>
                  <label className="label">Kalendername</label>
                  <input className="input" value={importForm.calendarName} onChange={(e) => setImportForm({ ...importForm, calendarName: e.target.value })} required />
                </div>
                <div className="grid grid-cols-[1fr,90px] gap-3">
                  <div>
                    <label className="label">Zeitzone</label>
                    <input className="input" value={importForm.timezone} onChange={(e) => setImportForm({ ...importForm, timezone: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Farbe</label>
                    <input type="color" className="input h-[42px]" value={importForm.color} onChange={(e) => setImportForm({ ...importForm, color: e.target.value })} />
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="label">Kalender</label>
                <select className="select" value={importForm.calendarId} onChange={(e) => setImportForm({ ...importForm, calendarId: e.target.value })}>
                  {calendars.map((calendar) => <option key={calendar.id} value={calendar.id}>{calendar.name}</option>)}
                </select>
              </div>
            )}
            <button className="btn btn-primary w-full" disabled={importing}>
              <UploadCloud size={17} />{importing ? 'Importiere...' : 'Import starten'}
            </button>
            {importResult && (
              <div className="rounded-md border border-border bg-bg/35 p-3 text-small text-ink-secondary">
                {importResult.ok
                  ? `${importResult.imported} importiert, ${importResult.updated} aktualisiert, ${importResult.skipped} uebersprungen.`
                  : importResult.error}
              </div>
            )}
          </section>
        </div>
      </form>

      <form onSubmit={add} className="rounded-xl border border-border bg-surface-card p-5 shadow-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="inline-flex items-center gap-2 text-h4"><Rss size={18} />Kalender abonnieren</h2>
          <Badge tone="muted">Sync</Badge>
        </div>
        <div className="grid gap-3 lg:grid-cols-[1fr,2fr,120px,auto] lg:items-end">
        <div>
          <label className="label">Name</label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <label className="label">ICS-URL</label>
          <input className="input font-mono" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required />
        </div>
        <div>
          <label className="label">Farbe</label>
          <input type="color" className="input h-[42px]" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
        </div>
        <button className="btn btn-primary">Abonnieren</button>
        </div>
      </form>

      <section className="grid gap-3">
        {items.map((item) => (
          <div key={item.id} className="card flex items-start gap-4">
            <span className="w-3 h-3 rounded-full mt-1.5" style={{ background: item.color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-h4">{item.name}</h2>
                <Badge tone={item.status === 'ACTIVE' ? 'success' : item.status === 'ERROR' ? 'danger' : 'muted'}>{item.status.toLowerCase()}</Badge>
              </div>
              <div className="font-mono text-small text-ink-muted truncate">{item.url}</div>
              <div className="text-small text-ink-secondary mt-2">
                {item.calendar._count?.events ?? 0} Termine · zuletzt {item.lastSyncedAt ? new Date(item.lastSyncedAt).toLocaleString('de-DE') : 'noch nicht synchronisiert'}
              </div>
              {item.lastError && <div className="text-small text-status-danger mt-1">{item.lastError}</div>}
            </div>
            <button className="btn btn-secondary" onClick={() => sync(item.id)}><RefreshCw size={16} />Sync</button>
            <button className="btn btn-danger" onClick={() => remove(item.id)}>Loeschen</button>
          </div>
        ))}
        {items.length === 0 && <div className="card text-center text-ink-muted">Noch keine Abos.</div>}
      </section>
    </div>
  );
}
